'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import StatusBadge from '@/components/ui/StatusBadge';
import LuxuryModal from '@/components/ui/LuxuryModal';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Booking {
  id: string;
  booking_reference: string;
  service_type: string;
  status: string;
  total_amount: number;
  payment_status: string;
  departure_date?: string;
  return_date?: string;
  pax_count?: number;
  destination?: string;
  itinerary?: Record<string, unknown>;
  created_at: string;
  metadata?: Record<string, unknown>;
  supplier_confirmation?: string;
  supplier_name?: string;
  refund_status?: string;
  amendment_status?: string;
}

interface BookingEvent {
  id: string;
  event_type: string;
  event_description: string;
  created_at: string;
  actor?: string;
  metadata?: Record<string, unknown>;
}

interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method?: string;
  created_at: string;
  reference?: string;
}

interface Review {
  rating: number;
  title: string;
  body: string;
}

type ModalMode =
  | 'detail' |'timeline' |'cancel' |'reschedule' |'amend' |'invoice' |'voucher' |'itinerary' |'payments' |'concierge' |'review' |'rebook'
  | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDateTime(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function formatCurrency(n: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
}
function daysUntil(d?: string) {
  if (!d) return null;
  const diff = new Date(d).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
}
function getJourneyName(b: Booking) {
  return (b.itinerary as Record<string, string> | undefined)?.title || b.service_type || 'Journey';
}
function getDestination(b: Booking) {
  return (b.itinerary as Record<string, string> | undefined)?.destination || b.destination || '—';
}
function getImage(b: Booking) {
  return (b.itinerary as Record<string, string> | undefined)?.image || 'https://images.unsplash.com/photo-1562804698-088120bf574f';
}

const SUPPLIER_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  booking_created: { label: 'Booking Created', color: 'text-blue-400' },
  pending_confirmation: { label: 'Pending Confirmation', color: 'text-amber-400' },
  confirmed: { label: 'Confirmed', color: 'text-emerald-400' },
  ticketed: { label: 'Ticketed', color: 'text-emerald-400' },
  voucher_issued: { label: 'Voucher Issued', color: 'text-emerald-400' },
  cancelled: { label: 'Cancelled', color: 'text-rose-400' },
  refunded: { label: 'Refunded', color: 'text-blue-400' },
  partially_refunded: { label: 'Partially Refunded', color: 'text-amber-400' },
  modified: { label: 'Modified', color: 'text-purple-400' },
  rescheduled: { label: 'Rescheduled', color: 'text-amber-400' },
  failed: { label: 'Failed', color: 'text-rose-400' },
  manual_review_required: { label: 'Manual Review', color: 'text-orange-400' },
};

const EVENT_ICONS: Record<string, string> = {
  booking_created: 'PlusCircleIcon',
  payment_received: 'CreditCardIcon',
  confirmed: 'CheckCircleIcon',
  ticketed: 'TicketIcon',
  voucher_issued: 'DocumentTextIcon',
  supplier_submission: 'PaperAirplaneIcon',
  amendment_requested: 'PencilSquareIcon',
  amendment_approved: 'CheckBadgeIcon',
  cancellation_requested: 'XCircleIcon',
  cancelled: 'XCircleIcon',
  refund_initiated: 'ArrowUturnLeftIcon',
  refunded: 'ArrowUturnLeftIcon',
  rescheduled: 'CalendarIcon',
  itinerary_generated: 'SparklesIcon',
  notification_sent: 'BellIcon',
  admin_action: 'ShieldCheckIcon',
  default: 'ClockIcon',
};

// ─── AI Concierge Panel ───────────────────────────────────────────────────────
function AIConciergePanel({ booking }: { booking: Booking }) {
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([
    {
      role: 'assistant',
      content: `Hello! I'm your AI Concierge for booking **${booking.booking_reference}** — your journey to ${getDestination(booking)}. I can help with visa requirements, packing tips, local recommendations, itinerary questions, or any booking concerns.`,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await fetch('/api/ai/concierge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          context: {
            booking_reference: booking.booking_reference,
            destination: getDestination(booking),
            journey: getJourneyName(booking),
            departure_date: booking.departure_date,
            status: booking.status,
          },
        }),
      });
      const data = await res.json();
      const reply = data?.data?.message || data?.message || 'I\'m here to help with your journey. Please feel free to ask any questions.';
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'I\'m temporarily unavailable. Please contact our support team for immediate assistance.' }]);
    }
    setLoading(false);
  };

  const quickPrompts = [
    'What documents do I need?',
    'Packing recommendations',
    'Local tips & culture',
    'Weather forecast',
  ];

  return (
    <div className="flex flex-col h-96">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 mb-3">
        {messages.map((msg, i) => (
          <div key={`msg-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-[rgba(201,168,76,0.15)] border border-[rgba(201,168,76,0.3)] flex items-center justify-center mr-2 flex-shrink-0 mt-0.5 text-xs">
                ✦
              </div>
            )}
            <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
              msg.role === 'user' ?'bg-[var(--primary)] text-black' :'bg-[var(--surface-3)] text-[var(--foreground)]'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="w-6 h-6 rounded-full bg-[rgba(201,168,76,0.15)] border border-[rgba(201,168,76,0.3)] flex items-center justify-center mr-2 flex-shrink-0 text-xs">✦</div>
            <div className="bg-[var(--surface-3)] px-3 py-2 rounded-xl flex gap-1 items-center">
              {[0, 1, 2].map((i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-[var(--muted-foreground)] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {quickPrompts.map((p) => (
            <button
              key={p}
              onClick={() => { setInput(p); }}
              className="px-2.5 py-1 rounded-full border border-[var(--border)] text-[10px] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-colors"
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Ask about your journey..."
          className="flex-1 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[var(--primary)]"
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className="px-3 py-2 rounded-lg bg-[var(--primary)] text-black text-xs font-semibold disabled:opacity-50"
        >
          <Icon name="PaperAirplaneIcon" size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── Booking Timeline ─────────────────────────────────────────────────────────
function BookingTimeline({ booking }: { booking: Booking }) {
  const [events, setEvents] = useState<BookingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTimeline = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/booking-management/timeline?booking_id=${booking.id}`);
        if (res.ok) {
          const data = await res.json();
          setEvents(data.data || []);
        } else {
          // Fallback mock timeline
          setEvents([
            { id: '1', event_type: 'booking_created', event_description: 'Booking created and submitted', created_at: booking.created_at, actor: 'Customer' },
            { id: '2', event_type: 'payment_received', event_description: `Payment of ${formatCurrency(booking.total_amount)} received`, created_at: booking.created_at, actor: 'Payment Gateway' },
            { id: '3', event_type: 'supplier_submission', event_description: 'Booking submitted to supplier for confirmation', created_at: booking.created_at, actor: 'System' },
            ...(booking.status === 'confirmed' ? [{ id: '4', event_type: 'confirmed', event_description: 'Booking confirmed by supplier', created_at: booking.created_at, actor: 'Supplier' }] : []),
          ]);
        }
      } catch {
        setEvents([
          { id: '1', event_type: 'booking_created', event_description: 'Booking created', created_at: booking.created_at, actor: 'Customer' },
        ]);
      }
      setLoading(false);
    };
    fetchTimeline();
  }, [booking.id, booking.created_at, booking.status, booking.total_amount]);

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[var(--muted-foreground)]">Complete chronological history of booking ref <span className="font-mono text-[var(--primary)]">{booking.booking_reference}</span></p>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SUPPLIER_STATUS_LABELS[booking.status]?.color || 'text-[var(--muted-foreground)]'} bg-[var(--surface-3)]`}>
          {SUPPLIER_STATUS_LABELS[booking.status]?.label || booking.status}
        </span>
      </div>
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--border)]" />
        <div className="space-y-4">
          {events.map((event, idx) => {
            const iconName = EVENT_ICONS[event.event_type] || EVENT_ICONS.default;
            const isLast = idx === events.length - 1;
            return (
              <div key={event.id} className={`flex gap-4 pl-0 ${isLast ? 'opacity-100' : 'opacity-90'}`}>
                <div className="relative z-10 flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                    isLast ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.1)]' : 'border-[var(--border)] bg-[var(--surface-2)]'
                  }`}>
                    <Icon name={iconName as Parameters<typeof Icon>[0]['name']} size={14} className={isLast ? 'text-[var(--primary)]' : 'text-[var(--muted-foreground)]'} />
                  </div>
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">{event.event_description}</p>
                      {event.actor && <p className="text-xs text-[var(--muted-foreground)] mt-0.5">by {event.actor}</p>}
                    </div>
                    <p className="text-xs text-[var(--muted-foreground)] whitespace-nowrap flex-shrink-0">{formatDateTime(event.created_at)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Payment History ──────────────────────────────────────────────────────────
function PaymentHistory({ booking }: { booking: Booking }) {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/customers/transactions?booking_id=${booking.id}`);
        if (res.ok) {
          const data = await res.json();
          setPayments(data.data || []);
        } else {
          setPayments([{
            id: '1',
            amount: booking.total_amount,
            currency: 'USD',
            status: booking.payment_status,
            payment_method: 'Card',
            created_at: booking.created_at,
            reference: `PAY-${booking.booking_reference}`,
          }]);
        }
      } catch {
        setPayments([{
          id: '1',
          amount: booking.total_amount,
          currency: 'USD',
          status: booking.payment_status,
          payment_method: 'Card',
          created_at: booking.created_at,
          reference: `PAY-${booking.booking_reference}`,
        }]);
      }
      setLoading(false);
    };
    fetchPayments();
  }, [booking.id, booking.total_amount, booking.payment_status, booking.created_at, booking.booking_reference]);

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--muted-foreground)]">All payment transactions for this booking</p>
        {booking.refund_status && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-400/10 text-blue-400 font-medium">
            Refund: {booking.refund_status}
          </span>
        )}
      </div>
      <div className="rounded-xl border border-[var(--border)] overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border)]" style={{ background: 'var(--surface-4)' }}>
              {['Reference', 'Amount', 'Method', 'Status', 'Date'].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-[var(--muted-foreground)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-4)]/50">
                <td className="px-3 py-2.5 font-mono text-[var(--muted-foreground)]">{p.reference || p.id.slice(0, 12)}</td>
                <td className="px-3 py-2.5 font-semibold">{formatCurrency(p.amount, p.currency)}</td>
                <td className="px-3 py-2.5 text-[var(--muted-foreground)]">{p.payment_method || 'Card'}</td>
                <td className="px-3 py-2.5">
                  <span className={`font-medium capitalize ${
                    p.status === 'completed' || p.status === 'captured' ? 'text-emerald-400' :
                    p.status === 'failed' ? 'text-rose-400' : 'text-amber-400'
                  }`}>{p.status}</span>
                </td>
                <td className="px-3 py-2.5 text-[var(--muted-foreground)]">{formatDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between pt-1 text-xs text-[var(--muted-foreground)]">
        <span>Total Paid</span>
        <span className="font-bold text-[var(--primary)]">{formatCurrency(payments.reduce((s, p) => s + p.amount, 0))}</span>
      </div>
    </div>
  );
}

// ─── Invoice View ─────────────────────────────────────────────────────────────
function InvoiceView({ booking }: { booking: Booking }) {
  const invoiceNum = `INV-${booking.booking_reference || booking.id.slice(0, 8).toUpperCase()}`;
  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</p>
          <p className="text-xs text-[var(--muted-foreground)]">Worldway × Abercrombie & Kent</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-[var(--primary)]">{invoiceNum}</p>
          <p className="text-xs text-[var(--muted-foreground)]">Issued: {formatDate(booking.created_at)}</p>
        </div>
      </div>
      <div className="border-t border-[var(--border)] pt-4 space-y-2">
        {[
          { label: 'Journey', value: getJourneyName(booking) },
          { label: 'Destination', value: getDestination(booking) },
          { label: 'Departure', value: formatDate(booking.departure_date) },
          { label: 'Return', value: formatDate(booking.return_date) },
          { label: 'Travelers', value: String(booking.pax_count || 1) },
          { label: 'Booking Ref', value: booking.booking_reference, mono: true },
          ...(booking.supplier_confirmation ? [{ label: 'Supplier Ref', value: booking.supplier_confirmation, mono: true }] : []),
        ].map(({ label, value, mono }) => (
          <div key={label} className="flex justify-between text-xs">
            <span className="text-[var(--muted-foreground)]">{label}</span>
            <span className={`font-medium ${mono ? 'font-mono' : ''}`}>{value}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-[var(--border)] pt-3 flex justify-between font-bold">
        <span>Total Amount</span>
        <span className="text-[var(--primary)]">{formatCurrency(booking.total_amount)}</span>
      </div>
      <div className="flex justify-between text-xs text-[var(--muted-foreground)]">
        <span>Payment Status</span>
        <span className="capitalize font-medium">{booking.payment_status}</span>
      </div>
      <button className="w-full py-2.5 rounded-xl bg-[var(--primary)] text-black text-xs font-bold mt-2 flex items-center justify-center gap-2">
        <Icon name="DocumentArrowDownIcon" size={14} />
        Download PDF Invoice
      </button>
    </div>
  );
}

// ─── Voucher View ─────────────────────────────────────────────────────────────
function VoucherView({ booking }: { booking: Booking }) {
  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-[rgba(201,168,76,0.4)] rounded-xl p-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-2">Travel Voucher</p>
        <p className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-serif)' }}>{getJourneyName(booking)}</p>
        <p className="text-sm text-[var(--muted-foreground)] mb-4">{getDestination(booking)}</p>
        <div className="bg-[var(--surface-3)] rounded-lg p-3 mb-4">
          <p className="text-xs text-[var(--muted-foreground)] mb-1">Voucher Code</p>
          <p className="text-lg font-mono font-bold text-[var(--primary)]">WL-{booking.booking_reference}</p>
        </div>
        {booking.supplier_confirmation && (
          <div className="bg-[var(--surface-3)] rounded-lg p-3 mb-4">
            <p className="text-xs text-[var(--muted-foreground)] mb-1">Supplier Confirmation</p>
            <p className="text-sm font-mono font-bold">{booking.supplier_confirmation}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-xs text-left">
          {[
            { label: 'Departure', value: formatDate(booking.departure_date) },
            { label: 'Return', value: formatDate(booking.return_date) },
            { label: 'Travelers', value: `${booking.pax_count || 1} guest${(booking.pax_count || 1) > 1 ? 's' : ''}` },
            { label: 'Status', value: booking.status, colored: true },
          ].map(({ label, value, colored }) => (
            <div key={label}>
              <p className="text-[var(--muted-foreground)]">{label}</p>
              <p className={`font-medium capitalize ${colored ? (SUPPLIER_STATUS_LABELS[value]?.color || '') : ''}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>
      <button className="w-full py-2.5 rounded-xl bg-[var(--primary)] text-black text-xs font-bold flex items-center justify-center gap-2">
        <Icon name="DocumentArrowDownIcon" size={14} />
        Download Voucher PDF
      </button>
    </div>
  );
}

// ─── AI Itinerary View ────────────────────────────────────────────────────────
function AIItineraryView({ booking }: { booking: Booking }) {
  const [itinerary, setItinerary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generateItinerary = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ai/itinerary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: booking.id,
          destination: getDestination(booking),
          journey_name: getJourneyName(booking),
          departure_date: booking.departure_date,
          return_date: booking.return_date,
          pax_count: booking.pax_count || 1,
        }),
      });
      const data = await res.json();
      setItinerary(data?.data?.itinerary || data?.itinerary || `**AI Itinerary for ${getJourneyName(booking)}**\n\nYour personalized journey to ${getDestination(booking)} has been crafted with care. Please contact your AI Concierge for the full detailed itinerary.`);
      setGenerated(true);
    } catch {
      setItinerary(`Your AI itinerary for ${getJourneyName(booking)} is being prepared. Please check back shortly or contact your AI Concierge.`);
      setGenerated(true);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-[rgba(201,168,76,0.05)] border border-[rgba(201,168,76,0.2)]">
        <div className="w-10 h-10 rounded-full bg-[rgba(201,168,76,0.1)] flex items-center justify-center text-lg">✦</div>
        <div>
          <p className="font-semibold text-sm">AI-Powered Itinerary</p>
          <p className="text-xs text-[var(--muted-foreground)]">{getJourneyName(booking)} · {getDestination(booking)}</p>
        </div>
      </div>
      {!generated ? (
        <div className="text-center py-6">
          <Icon name="SparklesIcon" size={32} className="text-[var(--primary)] mx-auto mb-3" />
          <p className="text-sm font-medium mb-2">Generate Your Personalized Itinerary</p>
          <p className="text-xs text-[var(--muted-foreground)] mb-4">Our AI will create a detailed day-by-day itinerary tailored to your journey.</p>
          <button
            onClick={generateItinerary}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl bg-[var(--primary)] text-black text-sm font-bold disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {loading ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Generating...</> : <><Icon name="SparklesIcon" size={14} /> Generate Itinerary</>}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-[var(--surface-3)] rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
            {itinerary}
          </div>
          <div className="flex gap-2">
            <button className="flex-1 py-2.5 rounded-xl bg-[var(--primary)] text-black text-xs font-bold flex items-center justify-center gap-2">
              <Icon name="DocumentArrowDownIcon" size={14} />
              Download PDF
            </button>
            <button onClick={() => { setGenerated(false); setItinerary(null); }} className="px-4 py-2.5 rounded-xl btn-ghost text-xs">
              Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Review Form ──────────────────────────────────────────────────────────────
function ReviewForm({ booking, onClose }: { booking: Booking; onClose: () => void }) {
  const [review, setReview] = useState<Review>({ rating: 5, title: '', body: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!review.title || !review.body) return;
    setLoading(true);
    try {
      await fetch('/api/bookings/' + booking.id + '/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(review),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-14 h-14 rounded-full bg-emerald-400/10 flex items-center justify-center mx-auto mb-4">
          <Icon name="StarIcon" size={28} className="text-emerald-400" />
        </div>
        <p className="font-semibold text-lg mb-2">Thank You!</p>
        <p className="text-sm text-[var(--muted-foreground)] mb-4">Your review has been submitted. It helps other travelers plan their journeys.</p>
        <button onClick={onClose} className="px-6 py-2 rounded-xl btn-primary text-sm">Close</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <p className="font-semibold mb-1">{getJourneyName(booking)}</p>
        <p className="text-xs text-[var(--muted-foreground)]">{getDestination(booking)} · {formatDate(booking.departure_date)}</p>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Your Rating</label>
        <div className="flex gap-2 justify-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setReview((r) => ({ ...r, rating: star }))}
              className={`text-2xl transition-transform hover:scale-110 ${star <= review.rating ? 'text-[var(--primary)]' : 'text-[var(--border)]'}`}
            >
              ★
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Review Title *</label>
        <input
          type="text"
          value={review.title}
          onChange={(e) => setReview((r) => ({ ...r, title: e.target.value }))}
          placeholder="Summarize your experience..."
          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Your Review *</label>
        <textarea
          rows={4}
          value={review.body}
          onChange={(e) => setReview((r) => ({ ...r, body: e.target.value }))}
          placeholder="Share your experience with other travelers..."
          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)] resize-none"
        />
      </div>
      <button
        onClick={submit}
        disabled={loading || !review.title || !review.body}
        className="w-full py-2.5 rounded-xl bg-[var(--primary)] text-black text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Submitting...</> : 'Submit Review'}
      </button>
    </div>
  );
}

// ─── Amendment Form ───────────────────────────────────────────────────────────
function AmendmentForm({ booking }: { booking: Booking }) {
  const [form, setForm] = useState({ type: '', details: '', pax_change: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.type || !form.details) return;
    setLoading(true);
    try {
      await fetch('/api/booking-management/amend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: booking.id, amendment_type: form.type, details: form.details, pax_change: form.pax_change }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-12 h-12 rounded-full bg-emerald-400/10 flex items-center justify-center mx-auto mb-3">
          <Icon name="CheckCircleIcon" size={24} className="text-emerald-400" />
        </div>
        <p className="font-semibold mb-2">Amendment Request Submitted</p>
        <p className="text-sm text-[var(--muted-foreground)]">Our team will review your request and respond within 48 hours. You will receive a notification once processed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-purple-400/10 border border-purple-400/20 rounded-xl p-4">
        <p className="text-sm font-semibold text-purple-400 mb-1">✏ Amendment Policy</p>
        <p className="text-xs text-[var(--muted-foreground)]">Amendment requests are subject to supplier availability and may incur fees. Changes requested 60+ days before departure are generally processed free of charge.</p>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Amendment Type *</label>
        <select
          value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="">Select amendment type</option>
          <option value="passenger_details">Passenger Details</option>
          <option value="room_type">Room / Cabin Type</option>
          <option value="meal_preference">Meal Preferences</option>
          <option value="special_assistance">Special Assistance</option>
          <option value="add_service">Add Service / Upgrade</option>
          <option value="remove_service">Remove Service</option>
          <option value="pax_count">Change Passenger Count</option>
          <option value="other">Other</option>
        </select>
      </div>
      {form.type === 'pax_count' && (
        <div>
          <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">New Passenger Count</label>
          <input
            type="number"
            min="1"
            value={form.pax_change}
            onChange={(e) => setForm((f) => ({ ...f, pax_change: e.target.value }))}
            className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
          />
        </div>
      )}
      <div>
        <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Amendment Details *</label>
        <textarea
          rows={3}
          value={form.details}
          onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
          placeholder="Please describe the changes you require..."
          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)] resize-none"
        />
      </div>
      <button
        onClick={submit}
        disabled={loading || !form.type || !form.details}
        className="w-full py-2.5 rounded-xl bg-[var(--primary)] text-black text-sm font-bold disabled:opacity-50"
      >
        {loading ? 'Submitting...' : 'Submit Amendment Request'}
      </button>
    </div>
  );
}

// ─── Booking Card ─────────────────────────────────────────────────────────────
function BookingCard({ booking, onAction, isFavourite, onToggleFavourite }: {
  booking: Booking;
  onAction: (b: Booking, mode: ModalMode) => void;
  isFavourite: boolean;
  onToggleFavourite: (id: string) => void;
}) {
  const days = daysUntil(booking.departure_date);
  const image = getImage(booking);
  const journeyName = getJourneyName(booking);
  const destination = getDestination(booking);
  const isUpcoming = ['confirmed', 'pending', 'processing', 'ticketed', 'voucher_issued'].includes(booking.status);
  const isCompleted = booking.status === 'completed';

  return (
    <div className="rounded-xl border border-[var(--border)] overflow-hidden luxury-shadow hover:border-[var(--primary)]/30 transition-all" style={{ background: 'var(--card)' }}>
      <div className="relative h-36 overflow-hidden">
        <AppImage src={image} alt={`${destination} travel destination`} fill sizes="(max-width: 768px) 100vw, 50vw" className="object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-2 right-2 flex gap-1.5">
          <button
            onClick={() => onToggleFavourite(booking.id)}
            className={`w-7 h-7 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors ${isFavourite ? 'bg-[var(--primary)] text-black' : 'bg-black/40 text-white hover:bg-[var(--primary)] hover:text-black'}`}
            title={isFavourite ? 'Remove from favourites' : 'Save to favourites'}
          >
            <Icon name="HeartIcon" size={12} variant={isFavourite ? 'solid' : 'outline'} />
          </button>
          {days !== null && days > 0 && (
            <div className="bg-black/50 backdrop-blur-sm px-2 py-0.5 rounded-full">
              <span className="text-[var(--primary)] font-bold text-xs">{days}d</span>
            </div>
          )}
        </div>
        <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
          <div>
            <p className="text-white font-semibold text-sm leading-tight truncate max-w-[160px]">{journeyName}</p>
            <p className="text-white/70 text-xs">{destination}</p>
          </div>
          <StatusBadge status={booking.status as Parameters<typeof StatusBadge>[0]['status']} size="sm" />
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <p className="text-[var(--muted-foreground)] text-[10px] uppercase tracking-wider">Ref</p>
            <p className="font-mono font-medium truncate">{booking.booking_reference}</p>
          </div>
          <div>
            <p className="text-[var(--muted-foreground)] text-[10px] uppercase tracking-wider">Departure</p>
            <p className="font-medium">{formatDate(booking.departure_date)}</p>
          </div>
          <div>
            <p className="text-[var(--muted-foreground)] text-[10px] uppercase tracking-wider">Total</p>
            <p className="font-semibold text-[var(--primary)]">{formatCurrency(booking.total_amount)}</p>
          </div>
        </div>

        {booking.supplier_confirmation && (
          <div className="flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]">
            <Icon name="CheckBadgeIcon" size={12} className="text-emerald-400 flex-shrink-0" />
            <span>Supplier: <span className="font-mono text-[var(--foreground)]">{booking.supplier_confirmation}</span></span>
          </div>
        )}

        <div className="flex flex-wrap gap-1">
          <button onClick={() => onAction(booking, 'detail')} className="flex-1 min-w-0 py-1.5 rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] text-[10px] font-semibold hover:bg-[var(--primary)]/20 transition-colors flex items-center justify-center gap-1">
            <Icon name="EyeIcon" size={11} /> Details
          </button>
          <button onClick={() => onAction(booking, 'timeline')} className="flex-1 min-w-0 py-1.5 rounded-lg btn-ghost text-[10px] font-medium flex items-center justify-center gap-1">
            <Icon name="ClockIcon" size={11} /> Timeline
          </button>
          <button onClick={() => onAction(booking, 'concierge')} className="flex-1 min-w-0 py-1.5 rounded-lg btn-ghost text-[10px] font-medium flex items-center justify-center gap-1 text-blue-400">
            <Icon name="ChatBubbleLeftRightIcon" size={11} /> Concierge
          </button>
        </div>

        <div className="flex flex-wrap gap-1 pt-0.5 border-t border-[var(--border)]/50">
          <button onClick={() => onAction(booking, 'invoice')} className="px-2 py-1 rounded text-[10px] text-[var(--muted-foreground)] hover:text-[var(--primary)] flex items-center gap-1 transition-colors">
            <Icon name="DocumentTextIcon" size={10} /> Invoice
          </button>
          <button onClick={() => onAction(booking, 'voucher')} className="px-2 py-1 rounded text-[10px] text-[var(--muted-foreground)] hover:text-[var(--primary)] flex items-center gap-1 transition-colors">
            <Icon name="TicketIcon" size={10} /> Voucher
          </button>
          <button onClick={() => onAction(booking, 'itinerary')} className="px-2 py-1 rounded text-[10px] text-[var(--muted-foreground)] hover:text-[var(--primary)] flex items-center gap-1 transition-colors">
            <Icon name="SparklesIcon" size={10} /> Itinerary
          </button>
          <button onClick={() => onAction(booking, 'payments')} className="px-2 py-1 rounded text-[10px] text-[var(--muted-foreground)] hover:text-[var(--primary)] flex items-center gap-1 transition-colors">
            <Icon name="CreditCardIcon" size={10} /> Payments
          </button>
          {isUpcoming && (
            <>
              <button onClick={() => onAction(booking, 'amend')} className="px-2 py-1 rounded text-[10px] text-purple-400 hover:bg-purple-400/10 flex items-center gap-1 transition-colors">
                <Icon name="PencilSquareIcon" size={10} /> Amend
              </button>
              <button onClick={() => onAction(booking, 'reschedule')} className="px-2 py-1 rounded text-[10px] text-amber-400 hover:bg-amber-400/10 flex items-center gap-1 transition-colors">
                <Icon name="CalendarIcon" size={10} /> Reschedule
              </button>
              <button onClick={() => onAction(booking, 'cancel')} className="px-2 py-1 rounded text-[10px] text-rose-400 hover:bg-rose-400/10 flex items-center gap-1 transition-colors">
                <Icon name="XCircleIcon" size={10} /> Cancel
              </button>
            </>
          )}
          {isCompleted && (
            <>
              <button onClick={() => onAction(booking, 'review')} className="px-2 py-1 rounded text-[10px] text-amber-400 hover:bg-amber-400/10 flex items-center gap-1 transition-colors">
                <Icon name="StarIcon" size={10} /> Review
              </button>
              <button onClick={() => onAction(booking, 'rebook')} className="px-2 py-1 rounded text-[10px] text-emerald-400 hover:bg-emerald-400/10 flex items-center gap-1 transition-colors">
                <Icon name="ArrowPathIcon" size={10} /> Rebook
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MyBookingsPortalContent() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'completed' | 'cancelled' | 'favourites'>('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [favourites, setFavourites] = useState<Set<string>>(new Set());
  const [cancelForm, setCancelForm] = useState({ reason: '', detail: '' });
  const [rescheduleForm, setRescheduleForm] = useState({ newDate: '', reason: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced'>('idle');
  const limit = 12;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusParam =
        filter === 'upcoming' ? '&status=confirmed,pending,processing,ticketed,voucher_issued' :
        filter === 'completed' ? '&status=completed' :
        filter === 'cancelled' ? '&status=cancelled,refunded' : '';
      const res = await fetch(`/api/bookings?page=${page}&limit=${limit}${statusParam}`);
      if (!res.ok) throw new Error('Failed to fetch bookings');
      const data = await res.json();
      setBookings(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Load favourites from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('wl_favourite_bookings');
      if (saved) setFavourites(new Set(JSON.parse(saved)));
    } catch { /* ignore */ }
  }, []);

  const toggleFavourite = (id: string) => {
    setFavourites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem('wl_favourite_bookings', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const syncBookingStatus = async () => {
    setSyncStatus('syncing');
    try {
      await fetch('/api/booking-management/sync', { method: 'POST' });
      await fetchBookings();
      setSyncStatus('synced');
      setTimeout(() => setSyncStatus('idle'), 3000);
    } catch {
      setSyncStatus('idle');
    }
  };

  const openModal = (booking: Booking, mode: ModalMode) => {
    setSelectedBooking(booking);
    setModalMode(mode);
    setActionSuccess('');
    if (mode === 'cancel') setCancelForm({ reason: '', detail: '' });
    if (mode === 'reschedule') setRescheduleForm({ newDate: '', reason: '' });
  };

  const handleCancellation = async () => {
    if (!cancelForm.reason || !selectedBooking) return;
    setActionLoading(true);
    try {
      await fetch('/api/booking-management/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: selectedBooking.id, reason: cancelForm.reason, detail: cancelForm.detail }),
      });
      setActionSuccess('Cancellation request submitted. Our team will process it within 24 hours and notify you by email.');
      fetchBookings();
    } catch {
      setActionSuccess('Request submitted. Our team will contact you shortly.');
    }
    setActionLoading(false);
  };

  const handleReschedule = async () => {
    if (!rescheduleForm.newDate || !selectedBooking) return;
    setActionLoading(true);
    try {
      await fetch('/api/booking-management/amend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: selectedBooking.id, amendment_type: 'reschedule', new_date: rescheduleForm.newDate, reason: rescheduleForm.reason }),
      });
      setActionSuccess('Reschedule request submitted. Our team will confirm availability within 48 hours.');
    } catch {
      setActionSuccess('Request submitted. Our team will contact you shortly.');
    }
    setActionLoading(false);
  };

  const displayedBookings = filter === 'favourites'
    ? bookings.filter((b) => favourites.has(b.id))
    : bookings;

  const stats = {
    total,
    upcoming: bookings.filter((b) => ['confirmed', 'pending', 'processing', 'ticketed', 'voucher_issued'].includes(b.status)).length,
    completed: bookings.filter((b) => b.status === 'completed').length,
    value: bookings.reduce((s, b) => s + (b.total_amount || 0), 0),
  };

  const getModalTitle = () => {
    if (!selectedBooking) return '';
    const titles: Record<NonNullable<ModalMode>, string> = {
      detail: 'Booking Details',
      timeline: 'Booking Timeline',
      cancel: 'Request Cancellation',
      reschedule: 'Request Reschedule',
      amend: 'Request Amendment',
      invoice: 'Invoice',
      voucher: 'Travel Voucher',
      itinerary: 'AI Itinerary',
      payments: 'Payment History',
      concierge: 'AI Concierge',
      review: 'Leave a Review',
      rebook: 'Rebook Journey',
    };
    return titles[modalMode!] || '';
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-2xl font-bold">My Bookings</h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">Manage all your WorldwayLuxe journeys in one place</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={syncBookingStatus}
            disabled={syncStatus === 'syncing'}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
              syncStatus === 'synced' ? 'border-emerald-400/30 text-emerald-400 bg-emerald-400/10' :
              syncStatus === 'syncing' ? 'border-[var(--border)] text-[var(--muted-foreground)]' :
              'border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] btn-ghost'
            }`}
          >
            <Icon name="ArrowPathIcon" size={13} className={syncStatus === 'syncing' ? 'animate-spin' : ''} />
            {syncStatus === 'syncing' ? 'Syncing...' : syncStatus === 'synced' ? 'Synced ✓' : 'Sync Status'}
          </button>
          <Link href="/journey-checkout" className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5">
            <Icon name="PlusIcon" size={14} /> New Booking
          </Link>
          <Link href="/customer-portal" className="btn-ghost px-3 py-2 rounded-lg text-sm">
            Portal →
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Bookings', value: total, icon: 'GlobeAltIcon', color: 'text-[var(--primary)]' },
          { label: 'Upcoming', value: stats.upcoming, icon: 'CalendarDaysIcon', color: 'text-emerald-400' },
          { label: 'Completed', value: stats.completed, icon: 'CheckCircleIcon', color: 'text-blue-400' },
          { label: 'Lifetime Value', value: formatCurrency(stats.value), icon: 'CurrencyDollarIcon', color: 'text-[var(--primary)]' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-[var(--border)] p-4 luxury-shadow" style={{ background: 'var(--card)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={14} className={s.color} />
              <p className="text-[10px] uppercase tracking-wider text-[var(--muted-foreground)]">{s.label}</p>
            </div>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters + View Toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'upcoming', 'completed', 'cancelled', 'favourites'] as const).map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              filter === f
                ? 'bg-[var(--primary)]/15 text-[var(--primary)] border border-[var(--primary)]/30'
                : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] border border-[var(--border)]'
            }`}
          >
            {f === 'favourites' ? '♥ Favourites' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 border border-[var(--border)] rounded-lg p-0.5">
          <button onClick={() => setView('grid')} className={`p-1.5 rounded ${view === 'grid' ? 'bg-[var(--primary)]/15 text-[var(--primary)]' : 'text-[var(--muted-foreground)]'}`}>
            <Icon name="Squares2X2Icon" size={14} />
          </button>
          <button onClick={() => setView('list')} className={`p-1.5 rounded ${view === 'list' ? 'bg-[var(--primary)]/15 text-[var(--primary)]' : 'text-[var(--muted-foreground)]'}`}>
            <Icon name="ListBulletIcon" size={14} />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-72 rounded-xl bg-[var(--surface-3)] animate-pulse" />
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-8 text-center">
          <Icon name="ExclamationTriangleIcon" size={28} className="text-rose-400 mx-auto mb-3" />
          <p className="text-sm text-rose-400 mb-3">{error}</p>
          <button onClick={fetchBookings} className="px-4 py-2 rounded-lg btn-primary text-sm">Retry</button>
        </div>
      )}

      {!loading && !error && displayedBookings.length === 0 && (
        <div className="rounded-xl border border-[var(--border)] p-12 text-center" style={{ background: 'var(--card)' }}>
          <Icon name="GlobeAltIcon" size={40} className="text-[var(--muted-foreground)] mx-auto mb-4" />
          <p className="font-semibold mb-2">{filter === 'favourites' ? 'No favourite bookings saved' : 'No bookings found'}</p>
          <p className="text-sm text-[var(--muted-foreground)] mb-5">
            {filter === 'favourites' ? 'Click the heart icon on any booking to save it as a favourite.' : 'Start your luxury journey with WorldwayLuxe.'}
          </p>
          <Link href="/all-journeys" className="btn-primary px-6 py-2.5 rounded-xl text-sm font-semibold">
            Browse Journeys
          </Link>
        </div>
      )}

      {!loading && !error && displayedBookings.length > 0 && (
        <>
          {view === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {displayedBookings.map((b) => (
                <BookingCard
                  key={b.id}
                  booking={b}
                  onAction={openModal}
                  isFavourite={favourites.has(b.id)}
                  onToggleFavourite={toggleFavourite}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-[var(--border)] overflow-hidden" style={{ background: 'var(--card)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)]" style={{ background: 'var(--surface-4)' }}>
                      {['', 'Ref', 'Journey', 'Destination', 'Departure', 'Guests', 'Total', 'Status', 'Actions'].map((col) => (
                        <th key={`th-${col}`} className="px-4 py-3 text-left text-[10px] uppercase tracking-wider font-semibold text-[var(--muted-foreground)] whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedBookings.map((b, i) => (
                      <tr key={b.id} className={`border-b border-[var(--border)]/50 hover:bg-[var(--surface-4)]/50 transition-colors ${i % 2 === 0 ? '' : 'bg-[var(--surface-2)]/30'}`}>
                        <td className="px-3 py-3">
                          <button onClick={() => toggleFavourite(b.id)} className={`${favourites.has(b.id) ? 'text-[var(--primary)]' : 'text-[var(--border)] hover:text-[var(--primary)]'} transition-colors`}>
                            <Icon name="HeartIcon" size={14} variant={favourites.has(b.id) ? 'solid' : 'outline'} />
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--muted-foreground)]">{b.booking_reference}</td>
                        <td className="px-4 py-3 font-medium max-w-[140px] truncate">{getJourneyName(b)}</td>
                        <td className="px-4 py-3 text-xs text-[var(--muted-foreground)]">{getDestination(b)}</td>
                        <td className="px-4 py-3 text-xs">{formatDate(b.departure_date)}</td>
                        <td className="px-4 py-3 text-xs text-center">{b.pax_count || 1}</td>
                        <td className="px-4 py-3 text-xs font-semibold">{formatCurrency(b.total_amount)}</td>
                        <td className="px-4 py-3"><StatusBadge status={b.status as Parameters<typeof StatusBadge>[0]['status']} size="sm" /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {[
                              { mode: 'detail' as ModalMode, icon: 'EyeIcon', title: 'Details' },
                              { mode: 'timeline' as ModalMode, icon: 'ClockIcon', title: 'Timeline' },
                              { mode: 'invoice' as ModalMode, icon: 'DocumentTextIcon', title: 'Invoice' },
                              { mode: 'concierge' as ModalMode, icon: 'ChatBubbleLeftRightIcon', title: 'Concierge' },
                            ].map(({ mode, icon, title }) => (
                              <button key={mode} onClick={() => openModal(b, mode)} title={title} className="p-1.5 rounded text-[var(--muted-foreground)] hover:text-[var(--primary)] hover:bg-[rgba(201,168,76,0.1)] transition-colors">
                                <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={13} />
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-[var(--muted-foreground)]">Showing {displayedBookings.length} of {total} bookings</p>
              <div className="flex gap-1">
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="p-2 rounded-lg btn-ghost disabled:opacity-40">
                  <Icon name="ChevronLeftIcon" size={14} />
                </button>
                <span className="text-xs text-[var(--muted-foreground)] px-3 py-2">Page {page}</span>
                <button disabled={page * limit >= total} onClick={() => setPage((p) => p + 1)} className="p-2 rounded-lg btn-ghost disabled:opacity-40">
                  <Icon name="ChevronRightIcon" size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      <LuxuryModal
        open={!!selectedBooking && !!modalMode}
        onClose={() => { setSelectedBooking(null); setModalMode(null); setActionSuccess(''); }}
        title={getModalTitle()}
        size="lg"
        footer={
          modalMode === 'cancel' ? (
            <>
              <button className="px-4 py-2 rounded-xl btn-ghost text-sm" onClick={() => setModalMode(null)}>Back</button>
              {!actionSuccess && (
                <button onClick={handleCancellation} disabled={actionLoading || !cancelForm.reason} className="px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-semibold disabled:opacity-50">
                  {actionLoading ? 'Submitting...' : 'Submit Cancellation'}
                </button>
              )}
            </>
          ) : modalMode === 'reschedule' ? (
            <>
              <button className="px-4 py-2 rounded-xl btn-ghost text-sm" onClick={() => setModalMode(null)}>Back</button>
              {!actionSuccess && (
                <button onClick={handleReschedule} disabled={actionLoading || !rescheduleForm.newDate} className="px-4 py-2 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50">
                  {actionLoading ? 'Submitting...' : 'Submit Request'}
                </button>
              )}
            </>
          ) : (
            <button className="px-4 py-2 rounded-xl btn-ghost text-sm" onClick={() => { setSelectedBooking(null); setModalMode(null); }}>Close</button>
          )
        }
      >
        {selectedBooking && (
          <div>
            {modalMode === 'detail' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {[
                    { label: 'Booking Reference', value: selectedBooking.booking_reference, mono: true },
                    { label: 'Status', value: selectedBooking.status, badge: true },
                    { label: 'Journey', value: getJourneyName(selectedBooking) },
                    { label: 'Destination', value: getDestination(selectedBooking) },
                    { label: 'Departure', value: formatDate(selectedBooking.departure_date) },
                    { label: 'Return', value: formatDate(selectedBooking.return_date) },
                    { label: 'Travelers', value: String(selectedBooking.pax_count || 1) },
                    { label: 'Total Amount', value: formatCurrency(selectedBooking.total_amount), highlight: true },
                    { label: 'Payment Status', value: selectedBooking.payment_status, capitalize: true },
                    { label: 'Booked On', value: formatDate(selectedBooking.created_at) },
                    ...(selectedBooking.supplier_confirmation ? [{ label: 'Supplier Confirmation', value: selectedBooking.supplier_confirmation, mono: true }] : []),
                    ...(selectedBooking.supplier_name ? [{ label: 'Supplier', value: selectedBooking.supplier_name }] : []),
                    ...(selectedBooking.refund_status ? [{ label: 'Refund Status', value: selectedBooking.refund_status, capitalize: true }] : []),
                    ...(selectedBooking.amendment_status ? [{ label: 'Amendment Status', value: selectedBooking.amendment_status, capitalize: true }] : []),
                  ].map(({ label, value, mono, badge, highlight, capitalize }) => (
                    <div key={label}>
                      <p className="text-[10px] uppercase tracking-wider text-[var(--muted-foreground)] mb-1">{label}</p>
                      {badge ? (
                        <StatusBadge status={value as Parameters<typeof StatusBadge>[0]['status']} size="sm" />
                      ) : (
                        <p className={`text-sm font-medium ${mono ? 'font-mono' : ''} ${highlight ? 'text-[var(--primary)]' : ''} ${capitalize ? 'capitalize' : ''}`}>{value}</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-[var(--border)]">
                  <p className="text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-3">Quick Actions</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { mode: 'timeline' as ModalMode, icon: 'ClockIcon', label: 'Timeline', color: '' },
                      { mode: 'invoice' as ModalMode, icon: 'DocumentTextIcon', label: 'Invoice', color: '' },
                      { mode: 'voucher' as ModalMode, icon: 'TicketIcon', label: 'Voucher', color: '' },
                      { mode: 'itinerary' as ModalMode, icon: 'SparklesIcon', label: 'AI Itinerary', color: '' },
                      { mode: 'payments' as ModalMode, icon: 'CreditCardIcon', label: 'Payments', color: '' },
                      { mode: 'concierge' as ModalMode, icon: 'ChatBubbleLeftRightIcon', label: 'Concierge', color: 'text-blue-400 border-blue-400/30' },
                    ].map(({ mode, icon, label, color }) => (
                      <button key={mode} onClick={() => setModalMode(mode)} className={`px-3 py-1.5 rounded-lg border border-[var(--border)] text-xs font-medium hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all flex items-center gap-1.5 ${color}`}>
                        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={12} /> {label}
                      </button>
                    ))}
                    {['confirmed', 'pending', 'processing', 'ticketed', 'voucher_issued'].includes(selectedBooking.status) && (
                      <>
                        <button onClick={() => setModalMode('amend')} className="px-3 py-1.5 rounded-lg border border-purple-400/30 text-xs font-medium text-purple-400 hover:bg-purple-400/10 transition-all flex items-center gap-1.5">
                          <Icon name="PencilSquareIcon" size={12} /> Amend
                        </button>
                        <button onClick={() => setModalMode('reschedule')} className="px-3 py-1.5 rounded-lg border border-amber-400/30 text-xs font-medium text-amber-400 hover:bg-amber-400/10 transition-all flex items-center gap-1.5">
                          <Icon name="CalendarIcon" size={12} /> Reschedule
                        </button>
                        <button onClick={() => setModalMode('cancel')} className="px-3 py-1.5 rounded-lg border border-rose-400/30 text-xs font-medium text-rose-400 hover:bg-rose-400/10 transition-all flex items-center gap-1.5">
                          <Icon name="XCircleIcon" size={12} /> Cancel
                        </button>
                      </>
                    )}
                    {selectedBooking.status === 'completed' && (
                      <>
                        <button onClick={() => setModalMode('review')} className="px-3 py-1.5 rounded-lg border border-amber-400/30 text-xs font-medium text-amber-400 hover:bg-amber-400/10 transition-all flex items-center gap-1.5">
                          <Icon name="StarIcon" size={12} /> Leave Review
                        </button>
                        <button onClick={() => setModalMode('rebook')} className="px-3 py-1.5 rounded-lg border border-emerald-400/30 text-xs font-medium text-emerald-400 hover:bg-emerald-400/10 transition-all flex items-center gap-1.5">
                          <Icon name="ArrowPathIcon" size={12} /> Rebook
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {modalMode === 'timeline' && <BookingTimeline booking={selectedBooking} />}
            {modalMode === 'invoice' && <InvoiceView booking={selectedBooking} />}
            {modalMode === 'voucher' && <VoucherView booking={selectedBooking} />}
            {modalMode === 'itinerary' && <AIItineraryView booking={selectedBooking} />}
            {modalMode === 'payments' && <PaymentHistory booking={selectedBooking} />}
            {modalMode === 'amend' && <AmendmentForm booking={selectedBooking} />}
            {modalMode === 'review' && <ReviewForm booking={selectedBooking} onClose={() => { setSelectedBooking(null); setModalMode(null); }} />}

            {modalMode === 'concierge' && (
              <div>
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[var(--border)]">
                  <div className="w-8 h-8 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center text-sm">✦</div>
                  <div>
                    <p className="font-semibold text-sm">AI Journey Concierge</p>
                    <p className="text-xs text-[var(--muted-foreground)]">Booking {selectedBooking.booking_reference} · {getJourneyName(selectedBooking)}</p>
                  </div>
                </div>
                <AIConciergePanel booking={selectedBooking} />
              </div>
            )}

            {modalMode === 'cancel' && (
              <div className="space-y-4">
                {actionSuccess ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 rounded-full bg-emerald-400/10 flex items-center justify-center mx-auto mb-3">
                      <Icon name="CheckCircleIcon" size={24} className="text-emerald-400" />
                    </div>
                    <p className="font-semibold mb-2">Request Submitted</p>
                    <p className="text-sm text-[var(--muted-foreground)]">{actionSuccess}</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4">
                      <p className="text-sm font-semibold text-rose-400 mb-1">⚠ Cancellation Policy</p>
                      <p className="text-xs text-[var(--muted-foreground)]">60+ days before departure: full refund minus admin fees. 30–59 days: 50% refund. Under 30 days: no refund. Please review the full policy before proceeding.</p>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Reason *</label>
                      <select value={cancelForm.reason} onChange={(e) => setCancelForm((f) => ({ ...f, reason: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]">
                        <option value="">Select a reason</option>
                        <option value="change_of_plans">Change of Plans</option>
                        <option value="medical">Medical / Health Reasons</option>
                        <option value="financial">Financial Reasons</option>
                        <option value="visa_denied">Visa Denied</option>
                        <option value="travel_advisory">Travel Advisory</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Additional Details</label>
                      <textarea rows={3} value={cancelForm.detail} onChange={(e) => setCancelForm((f) => ({ ...f, detail: e.target.value }))} placeholder="Please provide any additional context..." className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)] resize-none" />
                    </div>
                  </>
                )}
              </div>
            )}

            {modalMode === 'reschedule' && (
              <div className="space-y-4">
                {actionSuccess ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 rounded-full bg-emerald-400/10 flex items-center justify-center mx-auto mb-3">
                      <Icon name="CheckCircleIcon" size={24} className="text-emerald-400" />
                    </div>
                    <p className="font-semibold mb-2">Request Submitted</p>
                    <p className="text-sm text-[var(--muted-foreground)]">{actionSuccess}</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl p-4">
                      <p className="text-sm font-semibold text-amber-400 mb-1">📅 Reschedule Policy</p>
                      <p className="text-xs text-[var(--muted-foreground)]">Reschedule requests are subject to availability and may incur an amendment fee. Requests made 60+ days before departure are processed free of charge.</p>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Preferred New Date *</label>
                      <input type="date" value={rescheduleForm.newDate} onChange={(e) => setRescheduleForm((f) => ({ ...f, newDate: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Reason</label>
                      <textarea rows={3} value={rescheduleForm.reason} onChange={(e) => setRescheduleForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Please explain why you need to reschedule..." className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)] resize-none" />
                    </div>
                  </>
                )}
              </div>
            )}

            {modalMode === 'rebook' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-400/5 border border-emerald-400/20">
                  <Icon name="ArrowPathIcon" size={20} className="text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Rebook This Journey</p>
                    <p className="text-xs text-[var(--muted-foreground)]">{getJourneyName(selectedBooking)} · {getDestination(selectedBooking)}</p>
                  </div>
                </div>
                <p className="text-sm text-[var(--muted-foreground)]">We&apos;ll pre-fill your booking details from your previous journey to make rebooking quick and easy.</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-lg bg-[var(--surface-3)]">
                    <p className="text-[var(--muted-foreground)] mb-1">Previous Departure</p>
                    <p className="font-medium">{formatDate(selectedBooking.departure_date)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[var(--surface-3)]">
                    <p className="text-[var(--muted-foreground)] mb-1">Travelers</p>
                    <p className="font-medium">{selectedBooking.pax_count || 1} guests</p>
                  </div>
                </div>
                <Link
                  href={`/journey-checkout?rebook=${selectedBooking.id}`}
                  className="w-full py-2.5 rounded-xl bg-[var(--primary)] text-black text-sm font-bold flex items-center justify-center gap-2"
                >
                  <Icon name="ArrowPathIcon" size={14} />
                  Start Rebooking
                </Link>
              </div>
            )}
          </div>
        )}
      </LuxuryModal>
    </div>
  );
}
